// Character Loader - Loads and manages animated GLB character
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js';

export interface CharacterAnimations {
    idle?: THREE.AnimationClip;
    walk?: THREE.AnimationClip;
    point?: THREE.AnimationClip;
    celebrate?: THREE.AnimationClip;
    wave?: THREE.AnimationClip;
}

export interface LoadedCharacter {
    scene: THREE.Group;
    mixer: THREE.AnimationMixer;
    animations: CharacterAnimations;
    playAnimation: (name: keyof CharacterAnimations, options?: { loop?: boolean; fadeIn?: number }) => void;
    stopAnimation: (name: keyof CharacterAnimations, fadeOut?: number) => void;
    update: (delta: number) => void;
}

export class CharacterLoader {
    private static loader: GLTFLoader;
    private static dracoLoader: DRACOLoader;
    private static cache: Map<string, LoadedCharacter> = new Map();
    private static isInitialized = false;

    private static init(): void {
        if (this.isInitialized) return;

        // Create and configure DRACOLoader
        this.dracoLoader = new DRACOLoader();
        this.dracoLoader.setDecoderPath('https://www.gstatic.com/draco/versioned/decoders/1.5.7/');

        // Create GLTFLoader and set DRACOLoader
        this.loader = new GLTFLoader();
        this.loader.setDRACOLoader(this.dracoLoader);

        this.isInitialized = true;
    }

    static async load(
        url: string,
        extraAnimations: Record<string, string> = {},
        materialColors: Record<string, number> = {} // New: Map of material name part -> hex color
    ): Promise<LoadedCharacter> {
        this.init();

        // Check cache (key includes extras)
        const cacheKey = url + JSON.stringify(extraAnimations) + JSON.stringify(materialColors);
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey)!;
        }

        return new Promise((resolve, reject) => {
            this.loader.load(
                url,
                async (gltf) => {
                    const scene = gltf.scene;
                    const mixer = new THREE.AnimationMixer(scene);

                    // Set up character
                    scene.traverse((child) => {
                        if ((child as THREE.Mesh).isMesh) {
                            child.castShadow = true;
                            child.receiveShadow = true;
                            // Hide the "Cube" bounding box artifact
                            if (child.name === 'Cube' || child.name.includes('Box')) {
                                child.visible = false;
                                return;
                            }

                            console.log('Character Mesh:', child.name);

                            // Apply skin color change and fix lighting materials
                            const mesh = child as THREE.Mesh;
                            if (mesh.material) {
                                const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
                                materials.forEach((mat) => {
                                    const m = mat as THREE.MeshStandardMaterial;
                                    console.log('Material:', m.name, 'Map:', m.map ? 'Yes' : 'No');

                                    // Reverting roughness/metalness override to see if that fixes visibility
                                    // m.roughness = 0.8; 
                                    // m.metalness = 0.0;
                                    // m.needsUpdate = true; // Only update if we change something

                                    const matName = m.name.toLowerCase();

                                    // Check for custom overrides if provided
                                    let targetColor = null;
                                    for (const [key, color] of Object.entries(materialColors)) {
                                        if (matName.includes(key.toLowerCase())) {
                                            targetColor = color;
                                            break;
                                        }
                                    }

                                    // Apply custom color if found (only if materialColors was provided)
                                    if (targetColor !== null) {
                                        m.color.setHex(targetColor);
                                    }
                                });
                            }
                        }
                    });

                    // Map animations by name
                    const animations: CharacterAnimations = {};
                    const animationActions: Map<string, THREE.AnimationAction> = new Map();

                    // Helper to add animation
                    const addAnimation = (clip: THREE.AnimationClip, type?: keyof CharacterAnimations) => {
                        const name = clip.name.toLowerCase();
                        let targetType = type;

                        if (!targetType) {
                            if (name.includes('idle') || name.includes('stand')) targetType = 'idle';
                            else if (name.includes('walk') || name.includes('run')) targetType = 'walk';
                            else if (name.includes('point') || name.includes('gesture')) targetType = 'point';
                            else if (name.includes('celeb') || name.includes('dance')) targetType = 'celebrate';
                            else if (name.includes('wave') || name.includes('greeting')) targetType = 'wave';
                        }

                        if (targetType) {
                            animations[targetType] = clip;
                            animationActions.set(targetType, mixer.clipAction(clip));
                        }
                    };

                    // Process main file animations
                    gltf.animations.forEach((clip) => addAnimation(clip));

                    // Load extra animations
                    if (extraAnimations) {
                        const loadPromises = Object.entries(extraAnimations).map(([type, animUrl]) => {
                            return new Promise<void>((resolveAnim) => {
                                this.loader.load(animUrl, (animGltf) => {
                                    if (animGltf.animations.length > 0) {
                                        // Take the first animation from the file
                                        const clip = animGltf.animations[0];
                                        // Rename it to avoid conflicts (optional)
                                        clip.name = type;
                                        addAnimation(clip, type as keyof CharacterAnimations);
                                    }
                                    resolveAnim();
                                }, undefined, () => resolveAnim()); // Ignore errors for extras
                            });
                        });
                        await Promise.all(loadPromises);
                    }

                    // If no idle found, use first available
                    if (!animations.idle && gltf.animations.length > 0) {
                        animations.idle = gltf.animations[0];
                        animationActions.set('idle', mixer.clipAction(gltf.animations[0]));
                    }

                    let currentAction: THREE.AnimationAction | null = null;

                    const character: LoadedCharacter = {
                        scene,
                        mixer,
                        animations,

                        playAnimation(name, options = {}) {
                            const action = animationActions.get(name);
                            if (!action) return;

                            const { loop = true, fadeIn = 0.3 } = options;

                            if (currentAction && currentAction !== action) {
                                currentAction.fadeOut(fadeIn);
                            }

                            action.reset();
                            action.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, loop ? Infinity : 1);
                            action.clampWhenFinished = !loop;
                            action.fadeIn(fadeIn);
                            action.play();

                            currentAction = action;
                        },

                        stopAnimation(name, fadeOut = 0.3) {
                            const action = animationActions.get(name);
                            if (action) {
                                action.fadeOut(fadeOut);
                            }
                        },

                        update(delta) {
                            mixer.update(delta);
                        }
                    };

                    this.cache.set(cacheKey, character);
                    resolve(character);
                },
                undefined,
                (error) => {
                    console.error('Error loading character:', error);
                    reject(error);
                }
            );
        });
    }
}
